import { Alert, AlertIcon, Button, Flex, Spinner, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useRouterState } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { LicenseService } from "../../api/license"

export function requiredModule(pathname: string): string | null {
  const routes: Array<[RegExp, string]> = [
    [/\/inventory-count(?:\/|$)/i, "inventory_count"],
    [/\/reports\/inventory-count-alerts(?:\/|$)/i, "inventory_count"],
    [/\/ksef(?:\/|$)/i, "ksef_integration"],
    [/\/products\/(?:label-templates|label-assets)(?:\/|$)/i, "product_labels"],
    [
      /\/reports\/|\/warehouses\/(?:critical-stock|expiration-date)-reports?(?:\/|$)/i,
      "advanced_reports",
    ],
    [/\/dashboard(?:\/|$)/i, "operational_analytics"],
    [/\/production-orders(?:\/|$)|\/melduneks?(?:\/|$)/i, "production_orders"],
    [/\/technolog(?:y|ies)(?:\/|$)/i, "technology_management"],
    [/\/auto-replenishment(?:\/|$)/i, "auto_replenishment"],
  ]
  return routes.find(([pattern]) => pattern.test(pathname))?.[1] ?? null
}

export default function LicenseModuleRouteBoundary({
  children,
}: {
  children: ReactNode
}) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const moduleKey = requiredModule(pathname)
  const summary = useQuery({
    queryKey: ["license", "summary"],
    queryFn: LicenseService.getSummary,
    enabled: moduleKey !== null,
    refetchInterval: 60_000,
  })

  if (!moduleKey) return <>{children}</>
  if (summary.isPending) {
    return (
      <Flex justify="center" py={12}>
        <Spinner />
      </Flex>
    )
  }
  if (summary.isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text flex="1">
          Nie można sprawdzić dostępu do modułu. Odśwież licencję i spróbuj
          ponownie.
        </Text>
        <Button
          size="sm"
          onClick={() => void summary.refetch()}
          isLoading={summary.isFetching}
        >
          Ponów
        </Button>
      </Alert>
    )
  }
  const decision = summary.data?.modules?.find((item) => item.key === moduleKey)
  if (!decision?.enabled) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Text>
          Moduł {moduleKey} jest niedostępny w bieżącej licencji (
          {decision?.reason ?? "invalid_contract"}).
        </Text>
      </Alert>
    )
  }
  return <>{children}</>
}
